import React from 'react';
import { makeStyles } from '@material-ui/core';
import { formatPercent } from 'utils';
import { benchmarkFilter, benchmarkSort, formatName } from './benchmarks';
import moment from 'moment';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    gap: '0.5rem',
    paddingTop: '1rem'
  },
  nonReportWrapper: {
    '@media(max-width: 768px)': {
      maxHeight: '190px',
      overflow: 'auto',
      display: 'block'
    }
  },
  list: {
    paddingLeft: '1rem',
    marginTop: '0.25rem',
    flexWrap: 'wrap'
  },
  nonReportList: {
    '& li': {
      '@media(max-width: 900px)': {
        width: '100% !important'
      }
    }
  },
  scrollableList: {
    '@media(min-width: 769px)': {
      maxHeight: '190px',
      overflow: 'auto'
    }
  },
  column: {
    '@media(max-width: 900px)': {
      flex: '1 !important'
    }
  }
});

export const CustomLegend = ({
  payload,
  isBig = false,
  getLastEntry = (name: string) => name,
  benchmarks = [],
  toggles = {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggle = () => {},
  isForPdf = true,
  earliestDates,
  getExcludedPolicies
}: any) => {
  const classes = useStyles();
  const columnLimit = 9;
  const groupableKeys = [
    'Cash - Bank of England Base Rate',
    'Inflation - Consumer Price Index (CPI)',
    'Inflation - Retail Price Index (RPI)'
  ];

  let size = '14px';
  if (isBig) {
    size = '16px';
  }
  if (isForPdf) {
    size = '12px';
  }

  const getItems = (
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    filterBy = (entry: any) => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
    sortBy = (a: any, b: any) => {}
  ) => {
    return payload.filter(filterBy).sort(sortBy);
  };

  const getNullableLastEntry = (name: string) => {
    return getExcludedPolicies().includes(name) ? '-' : getLastEntry(name);
  };

  const portfolioItems = getItems(
    (entry: any) => {
      return !benchmarkFilter(benchmarks, entry.value);
    },
    (a: any, b: any) => {
      const aValue = getNullableLastEntry(a.value);
      const bValue = getNullableLastEntry(b.value);
      if (aValue === '-' && bValue === '-') {
        return 0;
      }
      if (aValue === '-') {
        return 1;
      }
      if (bValue === '-') {
        return -1;
      }
      return bValue - aValue;
    }
  );

  const benchmarkItems = getItems((entry: any) => {
    return benchmarkFilter(benchmarks, entry.value);
  }, benchmarkSort(benchmarks));

  const isToggled = (value: string) => {
    if (value in toggles) {
      return toggles[value];
    }
    return true;
  };

  const allBenchmarksSelected = () => {
    return benchmarkItems.every((entry: any) => {
      return isToggled(entry.value);
    });
  };

  const toggleAll = () => {
    const value = !allBenchmarksSelected();
    benchmarkItems
      .filter((entry: any) => {
        return value ? !isToggled(entry.value) : true;
      })
      .forEach((entry: any) => {
        toggle(entry.value, value);
      });
  };

  const getNameIncludingLastEntry = (name: string, display_name = null) => {
    const date =
      earliestDates && earliestDates[name]
        ? moment(earliestDates[name]).format('DD/MM/YYYY')
        : null;
    const value =
      getNullableLastEntry(name) === '-'
        ? '-'
        : formatPercent(getLastEntry(name), true);
    return `${formatName(display_name || name)}${date ? ` (${date})` : ''}: ${value}`;
  };

  const getListItem = (
    entry: any,
    key: string | number,
    canToggle = true,
    shouldWrap = false
  ) => {
    return (
      <div
        style={{
          display: 'flex',
          fontSize: size,
          width: shouldWrap ? '50%' : '100%'
        }}
        key={`item-${key}`}
        onClick={() => {
          if (!canToggle) return;
          toggle(entry.value, !isToggled(entry.value));
        }}
      >
        <div
          style={{
            backgroundColor: isToggled(entry.value) ? entry.color : '#ccc',
            width: size,
            minWidth: size,
            height: size,
            minHeight: size,
            marginRight: '0.2rem',
            borderRadius: '50%',
            marginTop: '2px'
          }}
        />
        <div style={{ flexGrow: 1 }}>
          {getNameIncludingLastEntry(entry.value, entry.display_value)}
        </div>
      </div>
    );
  };

  const getList = (items: any[], canToggle = true) => {
    const groups = {} as any;
    items.forEach((entry: any) => {
      const group = groupableKeys.find((group: string) => {
        return entry.value.includes(group);
      });

      if (group) {
        if (!(group in groups)) {
          groups[group] = [];
        }
        groups[group].push(entry);
      } else {
        groups[entry.value] = [entry];
      }
    });

    const shouldWrap = groups.length > columnLimit;

    return (
      <div
        className={`${classes.list} ${!isForPdf ? `${classes.scrollableList} ${classes.nonReportList}` : ''}`}
        style={{
          cursor: canToggle ? 'pointer' : 'default',
          display: shouldWrap ? 'flex' : 'block'
        }}
      >
        {Object.keys(groups).map((key: string) => {
          const group = groups[key];
          const entry = group[0] as any;
          const groupedItems = group.slice(1);

          if (groupedItems.length) {
            return (
              <div key={key}>
                {getListItem(entry, key, canToggle, shouldWrap)}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    padding: '0.1rem 1rem'
                  }}
                >
                  {groupedItems.map((item: any, innerIndex: number) => {
                    item.display_value = item.value.split(key)[1];
                    return (
                      <div key={innerIndex} style={{ paddingRight: '1rem' }}>
                        {getListItem(item, innerIndex, canToggle, shouldWrap)}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          return getListItem(entry, key, canToggle, shouldWrap);
        })}
      </div>
    );
  };

  return (
    <div
      className={`${classes.wrapper} ${isForPdf ? '' : classes.nonReportWrapper}`}
    >
      <div className={isForPdf ? '' : classes.column} style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, margin: '0 1rem' }}>Portfolio</div>
        {getList(portfolioItems)}
      </div>
      {benchmarkItems.length > 0 && (
        <div className={isForPdf ? '' : classes.column} style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ fontWeight: 600, margin: '0 1rem' }}>Benchmarks</div>
            {!isForPdf && (
              <div onClick={toggleAll} className="dashboardLink">
                {allBenchmarksSelected() ? 'Deselect' : 'Select'} all
              </div>
            )}
          </div>

          {getList(benchmarkItems)}
        </div>
      )}
    </div>
  );
};
